import { differenceInDays } from 'date-fns';

import { fg } from '@confluence/feature-gating';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ENROLMENT_WINDOW_DAYS } from '../utils/enrolmentWindow.ts';

import { useConnectorOnboardingState } from './useConnectorOnboardingState.ts';

/**
 * Hook for consumers to check if connector onboarding is enabled.
 * Use this to conditionally render the ConnectorPromoSidebar entry point component.
 *
 * Eligibility criteria:
 * - Rovo must be enabled
 * - User must have admin access
 * - User must be a founding admin (exclude nth users)
 * - User must not have dismissed the sidebar promo
 * - User must be within `ENROLMENT_WINDOW_DAYS` of their enrolment date
 * - The tenant must be in the rollout gate
 *
 * @example
 * ```tsx
 * const { isEnabled } = useIsConnectorOnboardingEnabled();
 *
 * {isEnabled && <ConnectorPromoSidebar />}
 * ```
 */
export function useIsConnectorOnboardingEnabled(): { isEnabled: boolean } {
	const { isRovoEnabled } = useSessionData();
	const isAdmin = useIsAdmin();

	const shouldSkipForPreConditions = !isRovoEnabled || !isAdmin;

	const {
		onboardingState,
		loading: isOnboardingStateLoading,
		error: onboardingStateError,
	} = useConnectorOnboardingState({
		skip: !fg('twcg-1489-cc-3p-connector-onboarding-rollout') || shouldSkipForPreConditions,
	});

	if (shouldSkipForPreConditions || isOnboardingStateLoading) {
		return { isEnabled: false };
	}

	// Unauthorized errors are already marked as handled inside useConnectorOnboardingState.
	// Non-unauthorized errors are left unhandled so they reach error monitoring.
	if (onboardingStateError) {
		return { isEnabled: false };
	}

	const { enrolmentDate, promoDismissed } = onboardingState ?? {};

	// The sidebar is the only remaining promo surface, so a sidebar dismissal
	// (on its own or as part of 'both') disables the experiment outright.
	if (promoDismissed === 'sidebar' || promoDismissed === 'both') {
		return { isEnabled: false };
	}

	// Only show to users within the enrolment window
	const isWithinEnrolmentWindow =
		Boolean(enrolmentDate) &&
		differenceInDays(new Date(), new Date(enrolmentDate as string)) < ENROLMENT_WINDOW_DAYS;

	if (!isWithinEnrolmentWindow) {
		return { isEnabled: false };
	}

	return { isEnabled: fg('twcg-1489-cc-3p-connector-onboarding-rollout') };
}
